import { PipelinesTablePanel } from '@/features/pipelines/components/PipelinesTablePanel';
import { usePipelinesPageViewModel } from '@/features/pipelines/hooks';
import { StackedMetricsStatusChartSection } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import { EntityDailyHistoryPanel } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import { getDetailLabel } from '@/shared/copy/details';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Stack } from '@/ui/layout/Stack';
import { Text } from '@/ui/primitives';
import type { ReactElement } from 'react';

export const PipelinesPage = (): ReactElement => {
  const {
    dailyHistoryDayKeys,
    dailyHistoryDateRangeLabel,
    dailyHistoryLoading,
    dailyHistoryHasError,
    dailyHistoryNoRowsText,
    dailyHistoryPage,
    dailyHistoryPageSize,
    dailyHistoryRows,
    expandedRowIds,
    dailyHistorySortDirection,
    dailyHistorySortKey,
    dailyHistoryTotalCount,
    metricSectionProps,
    setDailyHistoryPage,
    setDailyHistoryPageSize,
    setDailyHistorySortDirection,
    setDailyHistorySortKey,
    setExpandedRowIds,
    tablePanelProps,
  } = usePipelinesPageViewModel();

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Pipelines
        </Text>
      }
      charts={<StackedMetricsStatusChartSection {...metricSectionProps} />}
      content={
        <Stack gap="2xl">
          <EntityDailyHistoryPanel
            title="Pipeline History"
            dateRangeLabel={dailyHistoryDateRangeLabel}
            dayKeys={dailyHistoryDayKeys}
            entityLabel="Pipeline"
            activityLabel="runs"
            rows={dailyHistoryRows}
            loading={dailyHistoryLoading}
            showErrorState={dailyHistoryHasError}
            noRowsText={dailyHistoryNoRowsText}
            expandedRowIds={expandedRowIds}
            onExpandedRowIdsChange={setExpandedRowIds}
            sortKey={dailyHistorySortKey}
            sortDirection={dailyHistorySortDirection}
            sortOptions={[{ key: 'name', label: getDetailLabel('pipeline') }]}
            onSortChange={(nextSortKey, nextSortDirection) => {
              setDailyHistorySortKey(nextSortKey);
              setDailyHistorySortDirection(nextSortDirection);
            }}
            page={dailyHistoryPage}
            pageSize={dailyHistoryPageSize}
            totalCount={dailyHistoryTotalCount}
            onPageChange={setDailyHistoryPage}
            onPageSizeChange={setDailyHistoryPageSize}
            nameColumnMinWidthPx={180}
            nameColumnDefaultWidthPx={360}
          />
          <PipelinesTablePanel {...tablePanelProps} />
        </Stack>
      }
    />
  );
};
